import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

export default function BookingCalendar({ onDateTimeSelect, showPreparation = true }) {
  const { t, currentLanguage } = useTranslation();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const handleDateTimeSelect = (date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
    if (onDateTimeSelect) {
      onDateTimeSelect(date, time);
    }
  };

  // Получаем правильную локаль для форматирования даты
  const getDateLocale = () => {
    return currentLanguage === 'de' ? 'de-DE' : 'ru-RU';
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() - 1);
      return newMonth;
    });
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + 1);
      return newMonth;
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Добавляем пустые дни в начале месяца
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Добавляем дни месяца
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateAvailable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };

  // Доступное время
  const availableTimes = [
    '09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'
  ];

  const monthNames = [
    t('common.months.january'), t('common.months.february'), t('common.months.march'), t('common.months.april'), 
    t('common.months.may'), t('common.months.june'), t('common.months.july'), t('common.months.august'), 
    t('common.months.september'), t('common.months.october'), t('common.months.november'), t('common.months.december')
  ];

  const weekDays = [
    t('common.weekdays.monday'), t('common.weekdays.tuesday'), t('common.weekdays.wednesday'), 
    t('common.weekdays.thursday'), t('common.weekdays.friday'), t('common.weekdays.saturday'), t('common.weekdays.sunday')
  ];

  return (
    <div className="bg-white rounded-3xl shadow-xl p-6 border border-slate-200">
      <h3 className="text-xl font-semibold mb-6 text-slate-800 flex items-center gap-3">
        <Calendar className="h-6 w-6 text-primary-600" />
        {t('contact.calendar.title')}
      </h3>
      
      {/* Информация о времени */}
      <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-xl">
        <p className="text-sm text-blue-700 font-medium">
          {t('contact.calendar.timezone_note')}
        </p>
      </div>
      
      {/* Навигация по месяцам */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={goToPreviousMonth}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <ChevronLeft className="h-5 w-5 text-slate-600" />
        </button>
        <h4 className="text-lg font-semibold text-slate-800">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h4>
        <button
          onClick={goToNextMonth}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <ChevronRight className="h-5 w-5 text-slate-600" />
        </button>
      </div>

      {/* Дни недели */}
      <div className="grid grid-cols-7 gap-1 mb-3">
        {weekDays.map(day => (
          <div key={day} className="text-center text-sm font-medium text-slate-600 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Календарная сетка */}
      <div className="grid grid-cols-7 gap-1 mb-6">
        {getDaysInMonth(currentMonth).map((date, index) => {
          if (!date) {
            return <div key={index} className="h-10"></div>;
          }
          
          const isAvailable = isDateAvailable(date);
          const isSelected = selectedDate === date.toLocaleDateString(getDateLocale(), { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric' 
          });
          
          return (
            <button
              key={index}
              onClick={() => {
                if (isAvailable) {
                  setSelectedDate(date.toLocaleDateString(getDateLocale(), { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric' 
                  }));
                }
              }}
              disabled={!isAvailable}
              className={`h-10 rounded-lg text-sm font-medium transition-all ${
                isSelected
                  ? 'bg-primary-600 text-white shadow-lg'
                  : isAvailable
                  ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  : 'bg-slate-50 text-slate-400 cursor-not-allowed'
              }`}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Выбор времени */}
      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h4 className="font-medium text-slate-700 mb-3">
            {t('contact.available_time')}
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {availableTimes.map((time, index) => {
              const isSelected = selectedTime === time;
              
              return (
                <button
                  key={index}
                  onClick={() => handleDateTimeSelect(selectedDate, time)}
                  className={`p-3 rounded-xl text-sm font-medium transition-all ${
                    isSelected
                      ? 'bg-green-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {time}
                </button>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Информация о подготовке */}
      {showPreparation && (
        <div className="mt-6 p-4 bg-rose-50 rounded-2xl border border-rose-200">
          <h4 className="font-medium text-rose-800 mb-2 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {t('contact.preparation.title')}
          </h4>
          <ul className="text-sm text-rose-700 space-y-1">
            <li>• {t('contact.preparation.time')}</li>
            <li>• {t('contact.preparation.equipment')}</li>
            <li>• {t('contact.preparation.after')}</li>
          </ul>
        </div>
      )}
    </div>
  );
}
